import React, { useState, useCallback, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  BottomNavigation,
  BottomNavigationAction,
} from "@mui/material";
import { MdGroups, MdPayment, MdPending } from "react-icons/md";
import { showNotification } from "./NotificationProvider";
import Popup from "devextreme-react/popup";
import { TextBox, NumberBox, CheckBox, SelectBox } from "devextreme-react";

import { useDispatch, useSelector } from "react-redux";
import { getAllTeams, createTeam, updateTeam, deleteTeam } from "../store";
import TeamPaymentHistory from "./TeamPaymentHistory";
import PendingPayments from "./PendingPayments";

const TeamManagementPanel = () => {
  const dispatch = useDispatch();
  const teams = useSelector((state) => state.team.teams);
  const [tabValue, setTabValue] = useState(0);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [createPopupVisible, setCreatePopupVisible] = useState(false);
  const [editPopupVisible, setEditPopupVisible] = useState(false);
  const [newTeamName, setNewTeamName] = useState("");
  const [newMonthlyFee, setNewMonthlyFee] = useState(null);
  const [newPaymentDay, setNewPaymentDay] = useState(null);
  const [newPaymentEnabled, setNewPaymentEnabled] = useState(false);
  const [newUsdtAddress, setNewUsdtAddress] = useState("");
  const [newUsdtNetwork, setNewUsdtNetwork] = useState(null);

  useEffect(() => {
    fetchTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTeams = async () => {
    try {
      await dispatch(getAllTeams()).unwrap();
    } catch (error) {
      console.error("Failed to fetch teams:", error);
      showNotification("Error", "error", "Failed to fetch teams.");
    }
  };

  const handleCreateTeam = async () => {
    if (!newTeamName) {
      showNotification("Error", "error", "Team name is required.");
      return;
    }

    try {
      const teamData = {
        teamName: newTeamName,
        paymentEnabled: newPaymentEnabled,
      };

      // Add optional fields if they have values
      if (newMonthlyFee !== null && newMonthlyFee !== "") {
        teamData.monthlyFee = Number(newMonthlyFee);
      }

      if (newPaymentDay !== null && newPaymentDay !== "") {
        teamData.paymentDayOfMonth = Number(newPaymentDay);
      }

      if (newUsdtAddress) {
        teamData.usdtWalletAddress = newUsdtAddress;
      }

      if (newUsdtNetwork) {
        teamData.usdtNetwork = newUsdtNetwork;
      }

      await dispatch(createTeam(teamData)).unwrap();
      showNotification("Success", "success", "Team created successfully.");

      // Reset form
      setNewTeamName("");
      setNewMonthlyFee(null);
      setNewPaymentDay(null);
      setNewPaymentEnabled(false);
      setNewUsdtAddress("");
      setNewUsdtNetwork(null);
      setCreatePopupVisible(false);

      // Refresh teams
      await fetchTeams();
    } catch (error) {
      console.error("Failed to create team:", error);
      showNotification("Error", "error", "Failed to create team.");
    }
  };

  const handleEditTeam = (team) => {
    setSelectedTeam({
      ...team,
      monthlyFee: team.monthlyFee || null,
      paymentDayOfMonth: team.paymentDayOfMonth || null,
      paymentEnabled: team.paymentEnabled || false,
      usdtWalletAddress: team.usdtWalletAddress || "",
      usdtNetwork: team.usdtNetwork || null,
    });
    setEditPopupVisible(true);
  };

  const handleUpdateTeam = async () => {
    if (!selectedTeam?.teamName) {
      showNotification("Error", "error", "Team name is required.");
      return;
    }

    try {
      const updateData = {
        teamName: selectedTeam.teamName,
        paymentEnabled: selectedTeam.paymentEnabled || false,
      };

      // Add optional fields if they have values
      if (selectedTeam.monthlyFee !== null && selectedTeam.monthlyFee !== "") {
        updateData.monthlyFee = Number(selectedTeam.monthlyFee);
      }

      if (
        selectedTeam.paymentDayOfMonth !== null &&
        selectedTeam.paymentDayOfMonth !== ""
      ) {
        updateData.paymentDayOfMonth = Number(selectedTeam.paymentDayOfMonth);
      }

      if (selectedTeam.usdtWalletAddress) {
        updateData.usdtWalletAddress = selectedTeam.usdtWalletAddress;
      }

      if (selectedTeam.usdtNetwork) {
        updateData.usdtNetwork = selectedTeam.usdtNetwork;
      }

      await dispatch(
        updateTeam({ teamId: selectedTeam._id, data: updateData })
      ).unwrap();

      showNotification("Success", "success", "Team updated successfully.");
      setEditPopupVisible(false);
      setSelectedTeam(null);

      // Refresh teams
      await fetchTeams();
    } catch (error) {
      console.error("Failed to update team:", error);
      showNotification("Error", "error", "Failed to update team.");
    }
  };

  const handleDeleteTeam = async (teamId) => {
    if (!window.confirm("Are you sure you want to delete this team?")) {
      return;
    }

    try {
      await dispatch(deleteTeam(teamId)).unwrap();
      showNotification("Success", "success", "Team deleted successfully.");

      // Refresh teams
      await fetchTeams();
    } catch (error) {
      console.error("Failed to delete team:", error);
      showNotification(
        "Error",
        "error",
        error || "Failed to delete team. Team may have members."
      );
    }
  };

  const handleInputChange = (field, value) => {
    setSelectedTeam((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const renderCreatePopup = useCallback(
    () => (
      <div
        className="popup-create-team"
        style={{ display: "flex", flexDirection: "column", padding: "1rem" }}
      >
        <div style={{ marginBottom: "1.5rem" }}>
          <label
            style={{
              color: "#E0E6ED",
              fontSize: "1.3rem",
              marginBottom: "0.5rem",
              display: "block",
            }}
          >
            Team Name *
          </label>
          <TextBox
            value={newTeamName}
            onValueChanged={(e) => setNewTeamName(e.value)}
            placeholder="Enter Team Name"
            width="100%"
          />
        </div>

        <div style={{ marginBottom: "1.5rem" }}>
          <label
            style={{
              color: "#E0E6ED",
              fontSize: "1.3rem",
              marginBottom: "0.5rem",
              display: "block",
            }}
          >
            Monthly Fee (USD) - Optional
          </label>
          <NumberBox
            value={newMonthlyFee}
            onValueChanged={(e) => setNewMonthlyFee(e.value)}
            placeholder="Enter Monthly Fee"
            width="100%"
            min={0}
            showClearButton={true}
          />
        </div>

        <div style={{ marginBottom: "1.5rem" }}>
          <label
            style={{
              color: "#E0E6ED",
              fontSize: "1.3rem",
              marginBottom: "0.5rem",
              display: "block",
            }}
          >
            Payment Day of Month (1-31) - Optional
          </label>
          <NumberBox
            value={newPaymentDay}
            onValueChanged={(e) => setNewPaymentDay(e.value)}
            placeholder="Enter Payment Day"
            width="100%"
            min={1}
            max={31}
            showClearButton={true}
          />
        </div>

        <div style={{ marginBottom: "1.5rem" }}>
          <label
            style={{
              color: "#E0E6ED",
              fontSize: "1.3rem",
              marginBottom: "0.5rem",
              display: "block",
            }}
          >
            Payment Enabled
          </label>
          <CheckBox
            value={newPaymentEnabled}
            onValueChanged={(e) => setNewPaymentEnabled(e.value)}
            text="Enable payment system for this team"
          />
        </div>

        <div style={{ marginBottom: "1.5rem" }}>
          <label
            style={{
              color: "#E0E6ED",
              fontSize: "1.3rem",
              marginBottom: "0.5rem",
              display: "block",
            }}
          >
            USDT Wallet Address - Optional
          </label>
          <TextBox
            value={newUsdtAddress}
            onValueChanged={(e) => setNewUsdtAddress(e.value)}
            placeholder="Enter USDT Wallet Address"
            width="100%"
            showClearButton={true}
          />
        </div>

        <div style={{ marginBottom: "1.5rem" }}>
          <label
            style={{
              color: "#E0E6ED",
              fontSize: "1.3rem",
              marginBottom: "0.5rem",
              display: "block",
            }}
          >
            USDT Network - Optional
          </label>
          <SelectBox
            items={["TRC20", "ERC20", "BEP20"]}
            value={newUsdtNetwork}
            onValueChanged={(e) => setNewUsdtNetwork(e.value)}
            placeholder="Select Network"
            width="100%"
            showClearButton={true}
          />
        </div>

        <Button
          variant="contained"
          color="primary"
          onClick={handleCreateTeam}
          sx={{
            marginTop: "1rem",
            fontSize: "1.5rem",
            backgroundColor: "#556CD6",
          }}
        >
          Create Team
        </Button>
      </div>
    ),
    [
      newTeamName,
      newMonthlyFee,
      newPaymentDay,
      newPaymentEnabled,
      newUsdtAddress,
      newUsdtNetwork,
    ]
  );

  const renderEditPopup = useCallback(
    () => (
      <div
        className="popup-edit-team"
        style={{ display: "flex", flexDirection: "column", padding: "1rem" }}
      >
        <div style={{ marginBottom: "1.5rem" }}>
          <label
            style={{
              color: "#E0E6ED",
              fontSize: "1.3rem",
              marginBottom: "0.5rem",
              display: "block",
            }}
          >
            Team Name *
          </label>
          <TextBox
            value={selectedTeam?.teamName || ""}
            onValueChanged={(e) => handleInputChange("teamName", e.value)}
            placeholder="Enter Team Name"
            width="100%"
          />
        </div>

        <div style={{ marginBottom: "1.5rem" }}>
          <label
            style={{
              color: "#E0E6ED",
              fontSize: "1.3rem",
              marginBottom: "0.5rem",
              display: "block",
            }}
          >
            Monthly Fee (USD) - Optional
          </label>
          <NumberBox
            value={selectedTeam?.monthlyFee}
            onValueChanged={(e) => handleInputChange("monthlyFee", e.value)}
            placeholder="Enter Monthly Fee"
            width="100%"
            min={0}
            showClearButton={true}
          />
        </div>

        <div style={{ marginBottom: "1.5rem" }}>
          <label
            style={{
              color: "#E0E6ED",
              fontSize: "1.3rem",
              marginBottom: "0.5rem",
              display: "block",
            }}
          >
            Payment Day of Month (1-31) - Optional
          </label>
          <NumberBox
            value={selectedTeam?.paymentDayOfMonth}
            onValueChanged={(e) =>
              handleInputChange("paymentDayOfMonth", e.value)
            }
            placeholder="Enter Payment Day"
            width="100%"
            min={1}
            max={31}
            showClearButton={true}
          />
        </div>

        <div style={{ marginBottom: "1.5rem" }}>
          <label
            style={{
              color: "#E0E6ED",
              fontSize: "1.3rem",
              marginBottom: "0.5rem",
              display: "block",
            }}
          >
            Payment Enabled
          </label>
          <CheckBox
            value={selectedTeam?.paymentEnabled || false}
            onValueChanged={(e) => handleInputChange("paymentEnabled", e.value)}
            text="Enable payment system for this team"
          />
        </div>

        <div style={{ marginBottom: "1.5rem" }}>
          <label
            style={{
              color: "#E0E6ED",
              fontSize: "1.3rem",
              marginBottom: "0.5rem",
              display: "block",
            }}
          >
            USDT Wallet Address - Optional
          </label>
          <TextBox
            value={selectedTeam?.usdtWalletAddress || ""}
            onValueChanged={(e) =>
              handleInputChange("usdtWalletAddress", e.value)
            }
            placeholder="Enter USDT Wallet Address"
            width="100%"
            showClearButton={true}
          />
        </div>

        <div style={{ marginBottom: "1.5rem" }}>
          <label
            style={{
              color: "#E0E6ED",
              fontSize: "1.3rem",
              marginBottom: "0.5rem",
              display: "block",
            }}
          >
            USDT Network - Optional
          </label>
          <SelectBox
            items={["TRC20", "ERC20", "BEP20"]}
            value={selectedTeam?.usdtNetwork}
            onValueChanged={(e) => handleInputChange("usdtNetwork", e.value)}
            placeholder="Select Network"
            width="100%"
            showClearButton={true}
          />
        </div>

        <Button
          variant="contained"
          color="primary"
          onClick={handleUpdateTeam}
          sx={{
            marginTop: "1rem",
            fontSize: "1.5rem",
            backgroundColor: "#556CD6",
          }}
        >
          Update Team
        </Button>
      </div>
    ),
    [selectedTeam]
  );

  return (
    <div
      className="control-panel-team-list"
      style={{
        backgroundColor: "#1C273A",
        minHeight: "100vh",
      }}
    >
      {/* Navigation Tabs */}
      <BottomNavigation
        showLabels
        value={tabValue}
        onChange={(event, newValue) => {
          setTabValue(newValue);
        }}
        sx={{
          backgroundColor: "#1C273A",
          height: "80px",
          fontSize: "3rem",
          borderBottom: "2px solid #3A4663",
          "& .MuiBottomNavigationAction-root": {
            color: "#FFFFFF",
          },
          "& .Mui-selected": {
            color: "#00E396",
          },
        }}
      >
        <BottomNavigationAction
          label="Team List"
          icon={<MdGroups style={{ fontSize: "2rem" }} />}
        />
        <BottomNavigationAction
          label="Payment History"
          icon={<MdPayment style={{ fontSize: "2rem" }} />}
        />
        <BottomNavigationAction
          label="Pending Payments"
          icon={<MdPending style={{ fontSize: "2rem" }} />}
        />
      </BottomNavigation>

      {/* Tab 0: Team List */}
      {tabValue === 0 && (
        <>
          <div style={{ padding: "2rem" }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setCreatePopupVisible(true)}
              sx={{
                marginBottom: "2rem",
                fontSize: "1.5rem",
                backgroundColor: "#00E396",
                "&:hover": {
                  backgroundColor: "#00B377",
                },
              }}
            >
              Create New Team
            </Button>
          </div>

          <TableContainer
            component={Paper}
            sx={{
              width: "100%",
              margin: "1rem auto",
              backgroundColor: "#2E3B55",
              color: "#E0E6ED",
            }}
          >
            <Table
              sx={{
                "& .MuiTableCell-root": {
                  fontSize: "1.2rem",
                  color: "#E0E6ED",
                  borderBottom: "1px solid #3A4663",
                },
                "& .MuiTableHead-root": {
                  backgroundColor: "#3A4663",
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>Team Name</TableCell>
                  <TableCell>Monthly Fee (USD)</TableCell>
                  <TableCell>Payment Day</TableCell>
                  <TableCell>Payment Enabled</TableCell>
                  <TableCell>USDT Address</TableCell>
                  <TableCell>Network</TableCell>
                  <TableCell>Member Count</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {teams.map((team) => (
                  <TableRow key={team._id}>
                    <TableCell>
                      <span style={{ color: "#2196F3", fontWeight: "bold" }}>
                        {team.teamName}
                      </span>
                    </TableCell>
                    <TableCell>
                      {team.monthlyFee ? (
                        <span style={{ color: "#FF9800", fontWeight: "bold" }}>
                          ${team.monthlyFee}
                        </span>
                      ) : (
                        <span style={{ color: "#757575" }}>Not Set</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {team.paymentDayOfMonth ? (
                        <span>Day {team.paymentDayOfMonth}</span>
                      ) : (
                        <span style={{ color: "#757575" }}>Not Set</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {team.paymentEnabled ? (
                        <span style={{ color: "#00E396", fontWeight: "bold" }}>
                          ✓ Enabled
                        </span>
                      ) : (
                        <span style={{ color: "#F44336", fontWeight: "bold" }}>
                          ✗ Disabled
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {team.usdtWalletAddress ? (
                        <span
                          style={{
                            color: "#10B981",
                            fontFamily: "monospace",
                            fontSize: "1rem",
                          }}
                        >
                          {team.usdtWalletAddress.substring(0, 12)}...
                          {team.usdtWalletAddress.substring(
                            team.usdtWalletAddress.length - 8
                          )}
                        </span>
                      ) : (
                        <span style={{ color: "#757575" }}>Not Set</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {team.usdtNetwork ? (
                        <span style={{ color: "#10B981", fontWeight: "bold" }}>
                          {team.usdtNetwork}
                        </span>
                      ) : (
                        <span style={{ color: "#757575" }}>-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span style={{ color: "#00E396", fontWeight: "bold" }}>
                        {team.memberCount || 0} members
                      </span>
                    </TableCell>
                    <TableCell>
                      {new Date(team.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleEditTeam(team)}
                        sx={{ marginRight: "1rem" }}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        onClick={() => handleDeleteTeam(team._id)}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {teams.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <span style={{ color: "#757575", fontSize: "1.5rem" }}>
                        No teams found. Create your first team!
                      </span>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Tab 1: Payment History */}
      {tabValue === 1 && <TeamPaymentHistory />}

      {/* Tab 2: Pending Payments */}
      {tabValue === 2 && <PendingPayments />}

      {/* Create Team Popup */}
      <Popup
        width={600}
        height={550}
        showTitle={true}
        dragEnabled={false}
        hideOnOutsideClick={true}
        visible={createPopupVisible}
        onHiding={() => {
          setCreatePopupVisible(false);
          setNewTeamName("");
          setNewMonthlyFee(null);
          setNewPaymentDay(null);
          setNewPaymentEnabled(false);
        }}
        showCloseButton={true}
        container=".dx-viewport"
        title="Create New Team"
        animation={{
          show: { type: "slide", duration: 400, from: { top: -50 } },
        }}
        position={{ my: "center", at: "center", of: window }}
        contentRender={renderCreatePopup}
      />

      {/* Edit Team Popup */}
      <Popup
        width={600}
        height={550}
        showTitle={true}
        dragEnabled={false}
        hideOnOutsideClick={true}
        visible={editPopupVisible}
        onHiding={() => {
          setEditPopupVisible(false);
          setSelectedTeam(null);
        }}
        showCloseButton={true}
        container=".dx-viewport"
        title={`Edit Team: ${selectedTeam?.teamName || ""}`}
        animation={{
          show: { type: "slide", duration: 400, from: { top: -50 } },
        }}
        position={{ my: "center", at: "center", of: window }}
        contentRender={renderEditPopup}
      />
    </div>
  );
};

export default TeamManagementPanel;
